import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, CheckCircle, Warning } from '@phosphor-icons/react';
import LegalLayout from './LegalLayout';
import useSiteSettings from '../hooks/useSiteSettings';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DeleteMyAccountPage = () => {
  const s = useSiteSettings();
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = phone.trim().length >= 8 && confirmText === 'DELETE MY ACCOUNT' && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/account-deletion-request`, {
        phone: phone.trim(),
        email: email.trim() || null,
        reason: reason.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalLayout title="Delete My Account" lastUpdated="February 03, 2026" testId="delete-account-page">
      <p>
        Use this form to request deletion of your <strong>BeeWeddy</strong> account and all associated data. This page is
        provided so that you can request deletion <em>without logging in</em> — for example, if you have lost access to your
        phone number.
      </p>

      <p>
        <strong>If you can still log in, the fastest way to delete your account is in-app:</strong>{' '}
        Dashboard → Settings → Delete My Account.
      </p>

      <h2>What will be deleted</h2>
      <ul>
        <li>Your profile (name, phone, role, business details, photos)</li>
        <li>All bookings, package submissions, and saved packages you created</li>
        <li>Wallet balance, transaction history, and pending withdrawal requests</li>
        <li>Referral records (you will no longer earn from past invites)</li>
        <li>Galleries, services, and listings you authored</li>
        <li>Captain / professional event assignments</li>
      </ul>

      <h2>What we may keep (legal requirement)</h2>
      <ul>
        <li>Anonymized booking records for tax and audit (up to 7 years per the Income-Tax Act).</li>
        <li>Records of disputes / fraud investigations until they are resolved.</li>
      </ul>

      <p className="text-sm text-text-secondary">
        We will process valid requests within <strong>30 days</strong> as required by the DPDP Act 2023. You will receive an SMS
        once your data has been deleted.
      </p>

      {submitted ? (
        <div className="my-8 p-6 border-2 border-green-500 bg-green-50 rounded" data-testid="delete-success-block">
          <div className="flex items-start gap-3">
            <CheckCircle size={32} className="text-green-600 flex-shrink-0 mt-1" weight="fill" />
            <div>
              <h3 className="font-heading text-2xl text-green-800 mb-2 mt-0">Request Received</h3>
              <p className="text-sm text-green-900 mb-2">
                Your account deletion request has been submitted. Our team will verify your identity (via the phone
                number you provided) and complete deletion within <strong>30 days</strong>.
              </p>
              <p className="text-sm text-green-900">
                You will receive an SMS at <strong>{phone}</strong> once your data has been deleted. If you change your
                mind, please contact <a href={`mailto:${s.support_email}`} className="underline">{s.support_email}</a> within
                7 days.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="my-6 p-6 border border-[#E8E1D5] bg-white rounded space-y-4" data-testid="delete-account-form">
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900">
            <Warning size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
            <span>
              <strong>This action is permanent and cannot be undone.</strong> All your data will be permanently deleted within 30 days.
            </span>
          </div>

          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Registered Phone Number *</Label>
            <Input
              type="tel"
              placeholder="+91 9876543210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              className="rounded-none border-[#E8E1D5] mt-1"
              data-testid="delete-phone-input"
            />
            <p className="text-xs text-text-secondary mt-1">The phone number you used to create your account.</p>
          </div>

          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Contact Email (optional)</Label>
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none border-[#E8E1D5] mt-1"
              data-testid="delete-email-input"
            />
            <p className="text-xs text-text-secondary mt-1">If you'd like email confirmation when deletion is complete.</p>
          </div>

          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Reason (optional)</Label>
            <Textarea
              rows={3}
              placeholder="Help us improve — why are you leaving?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="rounded-none border-[#E8E1D5] mt-1"
              data-testid="delete-reason-input"
            />
          </div>

          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
              Type <span className="text-red-600 font-mono">DELETE MY ACCOUNT</span> to confirm *
            </Label>
            <Input
              type="text"
              placeholder="DELETE MY ACCOUNT"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              required
              className="rounded-none border-[#E8E1D5] mt-1 font-mono"
              data-testid="delete-confirm-input"
            />
          </div>

          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full bg-red-600 hover:bg-red-700 text-white rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="delete-submit-btn"
          >
            <Trash size={16} className="mr-2" />
            {submitting ? 'Submitting...' : 'Request Account Deletion'}
          </Button>
        </form>
      )}

      <h2>Need help instead?</h2>
      <p>
        If you'd rather pause notifications, change your phone number, or address a specific concern, we can probably help.
        Email <a href={`mailto:${s.support_email}`}>{s.support_email}</a> before deleting — we'll get back within 24 hours.
      </p>
    </LegalLayout>
  );
};

export default DeleteMyAccountPage;
